/**
 * SidebarCredits Component
 * Compact credit balance widget for the sidebar with buy action
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Coins, Plus } from 'lucide-react';
import { useCreditBalance } from '@/hooks/useCredits';
import { BuyCreditsDialog } from '@/components/credits/BuyCreditsDialog';
import { cn } from '@/lib/utils';

interface SidebarCreditsProps {
  isCollapsed: boolean;
}

export function SidebarCredits({ isCollapsed }: SidebarCreditsProps) {
  const { t } = useTranslation();
  const { data: balance, isLoading } = useCreditBalance();
  const [dialogOpen, setDialogOpen] = useState(false);

  const credits = balance?.credits ?? 0;

  return (
    <>
      {isCollapsed ? (
        <button
          onClick={() => setDialogOpen(true)}
          className={cn(
            'w-full flex flex-col items-center gap-1 py-2.5 rounded-lg',
            'transition-all hover:bg-gray-50 cursor-pointer group'
          )}
          title={`${credits} ${t('sidebar.credits')} – ${t('sidebar.buyCredits')}`}
        >
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[var(--color-primary-start)]/10 to-[var(--color-primary-end)]/10 flex items-center justify-center group-hover:from-[var(--color-primary-start)]/15 group-hover:to-[var(--color-primary-end)]/15 transition-all">
            <Coins className="w-4 h-4 text-[var(--color-primary-start)]" />
          </div>
          <span className={cn(
            'text-[11px] font-bold tabular-nums',
            isLoading ? 'text-gray-300' : 'text-[var(--color-charcoal)]'
          )}>
            {isLoading ? '–' : credits}
          </span>
        </button>
      ) : (
        <button
          onClick={() => setDialogOpen(true)}
          className={cn(
            'w-full mx-1 rounded-xl bg-gradient-to-br from-[var(--color-primary-start)]/[0.04] to-[var(--color-primary-end)]/[0.06]',
            'border border-[var(--color-primary-start)]/[0.08] p-2.5',
            'hover:from-[var(--color-primary-start)]/[0.07] hover:to-[var(--color-primary-end)]/[0.09]',
            'transition-all cursor-pointer group'
          )}
          title={t('sidebar.buyCredits')}
        >
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg gradient-bg flex items-center justify-center shrink-0">
              <Coins className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0 text-left">
              <div className={cn(
                'text-[17px] font-bold leading-tight tabular-nums',
                isLoading ? 'text-gray-300' : 'text-[var(--color-charcoal)]'
              )}>
                {isLoading ? '–' : credits}
              </div>
              <div className="text-[10px] text-[var(--color-charcoal)]/45 leading-tight font-medium">
                {t('sidebar.credits')}
              </div>
            </div>
            <div className={cn(
              'w-6 h-6 rounded-full gradient-bg flex items-center justify-center',
              'text-white shadow-sm group-hover:shadow-md transition-shadow'
            )}>
              <Plus className="w-3 h-3" strokeWidth={3} />
            </div>
          </div>
        </button>
      )}

      <BuyCreditsDialog isOpen={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  );
}
